import React, { useState } from "react";
import { Box, Button, Flex, FormControl, Input, Text, Textarea } from "@chakra-ui/react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { AppBar } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import { PRIMARY, BORDER, TEXT } from "../../components/requestUi";
import ImageUploader from "../../components/ImageUploader";
import { createRegisterOutsideApi } from "../../api/requests.api";
import { extractErrorMessage } from "../../api/client";
import { useEmployeeAuth } from "../../auth/EmployeeAuthContext";
import { EMP_BASE_PATH } from "../../config";

const BLUE_GRADIENT = "linear(to-r, #68A2FF, #2168DA)";

const Label = ({ children, required }) => (
  <Text fontSize="13px" fontWeight={600} mb="8px" color={TEXT}>
    {children}
    {required && <Text as="span" color="#EF2E14"> *</Text>}
  </Text>
);

const OutsideCreate = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const queryClient = useQueryClient();
  const { user } = useEmployeeAuth();
  const [images, setImages] = useState([]);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { checkinPlace: "", comment: "" },
  });

  const createMutation = useMutation({
    mutationFn: createRegisterOutsideApi,
    onSuccess: () => {
      toast.success("Tạo đăng ký chấm công ngoài thành công");
      queryClient.invalidateQueries({ queryKey: ["emp", "outside-list"] });
      navigate(`${EMP_BASE_PATH}/cham-cong-ngoai`);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Tạo đơn thất bại")),
  });

  const onSubmit = (v) => {
    if (images.length === 0) {
      toast.error("Vui lòng thêm ít nhất 1 ảnh xác nhận");
      return;
    }
    createMutation.mutate({
      employeeId: user?._id,
      checkinPlace: v.checkinPlace.trim(),
      comment: v.comment.trim(),
      confirmImage: images,
    });
  };

  const formEl = (
      <form onSubmit={handleSubmit(onSubmit)} style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <FormControl mb="18px" isInvalid={!!errors.checkinPlace}>
          <Label required>Địa điểm</Label>
          <Input
            placeholder="Điền địa điểm"
            borderColor={BORDER}
            borderRadius="8px"
            h="48px"
            fontSize="14px"
            {...register("checkinPlace", { required: "Vui lòng nhập địa điểm" })}
          />
          {errors.checkinPlace && (
            <Text fontSize="12px" color="red.500" mt="4px">{errors.checkinPlace.message}</Text>
          )}
        </FormControl>

        <FormControl mb="18px" isInvalid={!!errors.comment}>
          <Label required>Lý do</Label>
          <Controller
            control={control}
            name="comment"
            rules={{ required: "Vui lòng nhập lý do" }}
            render={({ field }) => (
              <Textarea {...field} placeholder="Điền lý do" borderColor={BORDER} borderRadius="8px" rows={4} fontSize="14px" />
            )}
          />
          {errors.comment && (
            <Text fontSize="12px" color="red.500" mt="4px">{errors.comment.message}</Text>
          )}
        </FormControl>

        <FormControl mb="20px">
          <Label required>Ảnh xác nhận</Label>
          <ImageUploader value={images} onChange={setImages} max={5} />
        </FormControl>

        <Flex gap="10px" mt="auto" pt="16px">
          <Button
            flex="1"
            h="48px"
            variant="outline"
            borderColor={PRIMARY}
            color={PRIMARY}
            borderRadius="10px"
            _hover={{ bg: "#EAF2FF" }}
            onClick={() => navigate(`${EMP_BASE_PATH}/cham-cong-ngoai`)}
          >
            Hủy
          </Button>
          <Button
            flex="1"
            h="48px"
            type="submit"
            isLoading={createMutation.isPending}
            bgGradient={BLUE_GRADIENT}
            color="white"
            borderRadius="10px"
            _hover={{ bgGradient: BLUE_GRADIENT, opacity: 0.92 }}
          >
            Thêm
          </Button>
        </Flex>
      </form>
  );

  // ===================== Bản WEB (desktop) =====================
  // Giữ nguyên form/validate/mutation, chỉ đặt trong khung trang kiểu admin.
  if (isDesktop) {
    return (
      <DesktopPage
        title="Đăng ký chấm công ngoài"
        right={<DesktopBack onClick={() => navigate(`${EMP_BASE_PATH}/cham-cong-ngoai`)} />}
      >
        <DesktopPanel p="24px">
          {formEl}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" minH="100dvh" display="flex" flexDirection="column">
      <AppBar title="Đăng ký chấm công ngoài" onBack={() => navigate(`${EMP_BASE_PATH}/cham-cong-ngoai`)} />

      <Box px="16px" pt="16px" pb="16px" flex="1" display="flex" flexDirection="column">
        {formEl}
      </Box>
    </Box>
  );
};

export default OutsideCreate;
